import { Injectable, Injector } from '@angular/core';
import { from } from 'rxjs';
import { forkJoin } from "rxjs/observable/forkJoin";
import { Observable } from 'rxjs';
import { BaseEditService, SchedulerModelFields, SchedulerModule } from '@progress/kendo-angular-scheduler';
import { parseDate } from '@progress/kendo-angular-intl';
import {
    WorkOrderScheduleService,
    AuthorizationService,
    BodyWorkOrderService,
    GAWorkOrderService,
    ProgramDetailsGAService,
    ProgramDetailsBodyService,
    HolidayScheduleService,
    EngineerScheduleService
} from '../ve.service';
import { AuthenticationService } from '../../../auth.service';
import { FilterableService } from '../filterable-service';

const WORK_ORDER_SERVICE = 'VE module specific work order service endpoint';
const PROGRAM_SERVICE = 'VE module specific program details endpoint';
const VACATION_SERVICE = 'VE module specific engineer vacation service endpoint';

// Event model field for scheduler.
const fields: SchedulerModelFields = (
    {
        id: 'Id',
        title: 'StudyTitle',
        start: 'PlannedStartDate',
        end: 'PlannedCompletionDate',
        assignedEngineer: 'AssignedEngineer',
        workOrderId: 'workOrderId',
        workOrderNumber: 'WorkOrderNumber'
    } as SchedulerModule);

@Injectable()
export class WorkOrderScheduleEditService extends BaseEditService<Event> {
    public readonly workOrderSchedulService: WorkOrderScheduleService;
    public readonly authorizationService: AuthorizationService;
    public readonly bodyWorkOrderService: BodyWorkOrderService;
    public readonly gaWorkOrderService: GAWorkOrderService;
    public readonly programDetailsGAService: ProgramDetailsGAService;
    public readonly programDetailsBodyService: ProgramDetailsBodyService;
    public readonly authService: AuthenticationService;
    public readonly holidayService: HolidayScheduleService;
    public readonly engineerSchedulerService: EngineerScheduleService;
    public engineerService = new FilterableService();
    public workOrderService = new FilterableService();
    public workOrderServiceFilterable = new FilterableService();
    public programFilterable = new FilterableService();
    public holidayFilterable = new FilterableService();
    public vacationFilterable = new FilterableService();
    public programsLoading = false;
    public workOrderSearchLoading = false;
    public loading = false;
    public resources = resourcesData;
    public module: any;
    public group: any = {
        resources: ['Virtual Engineers'],
        orientation: 'vertical'
    };

    constructor(
        private readonly injector: Injector
    ) {
        super(fields);
        this.workOrderSchedulService = injector.get(WorkOrderScheduleService);
        this.authorizationService = injector.get(AuthorizationService);
        this.bodyWorkOrderService = injector.get(BodyWorkOrderService);
        this.gaWorkOrderService = injector.get(GAWorkOrderService);
        this.programDetailsGAService = injector.get(ProgramDetailsGAService);
        this.programDetailsBodyService = injector.get(ProgramDetailsBodyService);
        this.authService = injector.get(AuthenticationService);
        this.holidayService = injector.get(HolidayScheduleService);
        this.engineerSchedulerService = injector.get(EngineerScheduleService);
        this.module = this.authService.getModule();
    }

    /**
     * Scheduler overwritten read function to return ve defined data to the scheduler.
     *
     * @returns {void}
     * @memberof WorkOrderScheduleEditService
     */
    public read(): void {

        this.fetch().subscribe(data => {
            this.getVacations(data);
            this.getHolidays(data);
            this.getModuleUsers(data);
            const result = data[0].concat(data[1], data[3]);
            this.data = result.map(item => this.readEvent(item));

            // Filter out any scheduled items that aren't assigned to the current engineers.
            const filteredOutList = []; // Filtered scheduled items for debugging purposes.
            this.data = this.data.filter(x => {
                const engineers = this.engineerService.value.map(z => z.value); // Capture the current engineers gmins.
                const assignedEngineers = this.holidayFilterable.value[0].AssignedEngineer; // Capture the current assigned engineers to the schedule item.
                // Find the intersection of assigned engineers and current engineers.
                const intersectedEngineers = engineers.filter(y => {
                    if (assignedEngineers.indexOf(y) === -1) {
                        return null;
                    }
                    return y;
                });

                // If there are any intersections between the two engineer arrays, return the scheduled item.
                if (intersectedEngineers.length) {
                    return x;
                } else {
                    filteredOutList.push(x);
                }
            });
            this.source.next(this.data);
            this.loading = false;
        });
    }

    /**
     * Get module users from 2nd element of data array and map to resources.
     *
     * @param {*} events
     * @memberof WorkOrderScheduleEditService
     */
    getModuleUsers(events) {
        events[2].sort((a, b) => a.FirstName.localeCompare(b.FirstName));
        const data = events[2].map(dataItem => (
            {
                text: dataItem.Name,
                value: dataItem.Gmin,
                color: '#1960AA'
            }
        ));
        
        // TODO: Fix this when Kendo enables disabling hour header for timelines in Angular.
        data.push({
            text: '',
            value: '',
            color: '#1960AA'
        });

        this.resources[0].data = data;
        this.engineerService.next(data);
    }

    /**
     * Get holidays from 0 element of data array and map to event model.
     *
     * @param {*} data
     * @memberof WorkOrderScheduleEditService
     */
    getHolidays(data) {
        data[0].forEach(element => {
            element.AssignedEngineer = data[2].map(x => x.Gmin);
            element.WorkOrderNumber = element.GlobalRegionDescription;
            element.isHoliday = true;
            element.StudyTitle = `${element.GlobalRegionDescription} Holiday`;
        });

        this.holidayFilterable.next(data[0]);
    }

    /**
     * Get Vacations from 3rd element of data array and map to event model.
     *
     * @param {*} data
     * @memberof WorkOrderScheduleEditService
     */
    getVacations(data) {
        data[3].forEach(element => {
            element.AssignedEngineer = element.Engineer;
            element.StudyTitle = element.Description;
            element.isVacation = true;
            element.WorkOrderNumber = element.Description;
        });

        this.vacationFilterable.next(data[3]);
    }

    /**
     * Get workorders defined by program id and map to resources object
     *
     * @param {*} program
     * @returns
     * @memberof WorkOrderScheduleEditService
     */
    getWorkOrders(program) {
        this.workOrderSearchLoading = true;
        return this.searchServices[`${this.module} ${WORK_ORDER_SERVICE}`](program)
            .then(result => {
                const data = result.map(dataItem => (
                    {
                        text: `${dataItem.WorkOrderNumber} ${dataItem.StudyTitle}`,
                        value: dataItem.WorkOrderId,
                    }
                ));
                this.resources[1].data = data;
                this.workOrderService.next(data);
                this.workOrderServiceFilterable.next(result);
                this.workOrderSearchLoading = false;
            })
            .catch(result => {
                this.workOrderSearchLoading = false;
            });
    }

    /**
     * Get programs and map them to their defined object in resources.
     *
     * @returns
     * @memberof WorkOrderScheduleEditService
     */
    getPrograms() {
        this.programsLoading = true;
        return this.searchServices[`${this.module} ${PROGRAM_SERVICE}`](true)
            .then(result => {
                const data = result.map(dataItem => (
                    {
                        text: dataItem.ProgramCode,
                        value: dataItem.ProgramId,
                    }
                ));
                this.resources[2].data = data;
                this.programFilterable.next(data.sort((a, b) => a.text.localeCompare(b.text)));
                this.programsLoading = false;
            })
            .catch(result => {
                this.programsLoading = false;
            });
    }

    /**
     * Get all of the endpoints needed for rendering events and return them as one object.
     * Each event type is an array in the object returned in the order called here.
     * @returns
     * @memberof WorkOrderScheduleEditService
     */
    getEvents() {
        const moduleNameContents = this.module.split('_');
        const reverseModuleName = `${moduleNameContents[1]}_${moduleNameContents[0]}`;
        return forkJoin([
            this.holidayService.read('/?activeOnly=true'),
            this.workOrderSchedulService.read(`/Module/${this.module}`),
            this.authorizationService.read(`/roleusers?role=${reverseModuleName}_Engineer`),
            this.engineerSchedulerService.read(),
        ]);
    }

    /**
     * Checks if a holiday is present on the selected time.
     *
     * @param {*} date
     * @returns
     * @memberof WorkOrderScheduleEditService
     */
    isHoliday(date) {
        const holidays = this.holidayFilterable.value.map(x => new Date(x.HolidayDate));
        const currentDate = new Date(date);
        return holidays.find(x => x.toString() === currentDate.toString());
    }

    /**
    * Scheduler overwritten method for saving. Updating, creating, and deleting types are overwritten
    * to use VE service endpoints.
    *
    * @protected
    * @param {Event[]} created
    * @param {Event[]} updated
    * @param {Event[]} deleted
    * @memberof WorkOrderScheduleEditService
    */
    protected save(created: Event[], updated: Event[], deleted: Event[]): void {
        if (deleted.length) {
            this.loading = true;
            this.removeServices[`${deleted[0]['isVacation'] ? VACATION_SERVICE : WORK_ORDER_SERVICE}`](deleted[0]['Id'])
                .then(() => {
                    this.read();
                });
        }

        if (updated.length) {
            this.loading = true;
            this.setDateTime(updated);
            this.updateServices[`${updated[0]['isVacation'] ? VACATION_SERVICE : WORK_ORDER_SERVICE}`](updated[0], updated[0]['Id'])
                .then(() => {
                    this.read();
                });
        }

        if (created.length) {
            this.createSchedule(created);
        }
    }

    public createSchedule(created: any) {
        this.loading = true;
        this.setDateTime(created);
        created[0]['ModuleName'] = this.module;
        this.createServices[`${created[0]['isVacation'] ? VACATION_SERVICE : WORK_ORDER_SERVICE}`](created[0])
            .then(() => {
                this.read();
            });
    }

    /**
     * Scheduler overwritten method for calling ve endpoints to retrieve scheduler data objects.
     *
     * @protected
     * @param {string} [action='']
     * @param {*} [data]
     * @returns {Observable<any[]>}
     * @memberof WorkOrderScheduleEditService
     */
    protected fetch(action = '', data?: any): Observable<any[]> {
        this.loading = true;
        this.getPrograms();
        return from(this.getEvents());
    }

    /**
     * Mapper for creating scheduler events defined by SchedulerModelFields type defined above.
     *
     * @private
     * @param {*} item
     * @returns {Event}
     * @memberof WorkOrderScheduleEditService
     */
    private readEvent(item: any): Event {
        return {
            ...item,
            PlannedStartDate: item.isHoliday ?
                this.setHolidayStartDateTime(item) :
                parseDate(item.PlannedStartDate),
            PlannedCompletionDate: item.isHoliday ?
                this.setHolidayEndDateTime(item) :
                parseDate(item.PlannedCompletionDate),
        };
    }

    /**
     * Defines the standard 8-5 hours for the planned starting and ending dates.
     *
     * @param {*} dataItem
     * @memberof WorkOrderScheduleEditService
     */
    setDateTime(dataItem: any) {
        const planedStartTime = new Date(dataItem[0]['PlannedStartDate']);
        planedStartTime.setHours(8, 0, 0, 0);
        dataItem[0]['PlannedStartDate'] = planedStartTime;

        const planedEndTime = new Date(dataItem[0]['PlannedCompletionDate']);
        planedEndTime.setHours(17, 0, 0, 0);
        dataItem[0]['PlannedCompletionDate'] = planedEndTime;
    }

    setHolidayStartDateTime(dataItem: any) {
        const planedStartTime = new Date(dataItem.HolidayDate);
        planedStartTime.setHours(9, 0, 0, 0);
        return planedStartTime;
    }

    setHolidayEndDateTime(dataItem: any) {
        const planedEndTime = new Date(dataItem.HolidayDate);
        planedEndTime.setHours(16, 0, 0, 0);
        return planedEndTime;
    }

    /**
     * Service object function mapper for common search endpoints between GA and Body.
     *
     * @readonly
     * @memberof WorkOrderScheduleEditService
     */
    get searchServices() {
        return {
            [`VE_GA ${WORK_ORDER_SERVICE}`]: (programId) => this.gaWorkOrderService.read(`/GetMyAssignable?programId=${programId}`),
            [`VE_Body ${WORK_ORDER_SERVICE}`]: (programId) => this.bodyWorkOrderService.read(`/GetMyAssignable?programId=${programId}`),
            [`VE_GA ${PROGRAM_SERVICE}`]: () => this.programDetailsGAService.read(`/withAssignableWos`),
            [`VE_Body ${PROGRAM_SERVICE}`]: () => this.programDetailsBodyService.read(`/withAssignableWos`),
        };
    }

    /**
     * Service object function mapper for common removal endpoints between work order and vacation endpoints.
     *
     * @readonly
     * @memberof WorkOrderScheduleEditService
     */
    get removeServices() {
        return {
            [`${WORK_ORDER_SERVICE}`]: (id) => this.workOrderSchedulService.remove(`/${id}`),
            [`${VACATION_SERVICE}`]: (id) => this.engineerSchedulerService.remove(`/${id}`),
        };
    }

    /**
     * Service object function mapper for common update endpoints between work order and vacation endpoints.
     *
     * @readonly
     * @memberof WorkOrderScheduleEditService
     */
    get updateServices() {
        return {
            [`${WORK_ORDER_SERVICE}`]: (input, id) => this.workOrderSchedulService.save(input, false, `/${id}`),
            [`${VACATION_SERVICE}`]: (input, id) => {
                input.Description = input.StudyTitle;
                input.Engineer = input.AssignedEngineer;
                return this.engineerSchedulerService.save(input, false, `/${id}`);
            },
        };
    }

    /**
     * Service object function mapper for common creating endpoints between work order and vacation endpoints.
     *
     * @readonly
     * @memberof WorkOrderScheduleEditService
     */
    get createServices() {
        return {
            [`${WORK_ORDER_SERVICE}`]: (input) => this.workOrderSchedulService.save(input, true),
            [`${VACATION_SERVICE}`]: (input) => {
                input.Description = input.StudyTitle;
                input.Engineer = input.AssignedEngineer;
                return this.engineerSchedulerService.save(input, true);
            },
        };
    }
}

const resourcesData: any[] = [
    {
        name: 'Virtual Engineers',
        data: [],
        field: 'AssignedEngineer',
        valueField: 'value',
        textField: 'text',
        colorField: 'color'
    },
    {
        name: 'Work Orders',
        data: [],
        field: 'WorkOrderId',
        valueField: 'value',
        textField: 'text',
    },
    {
        name: 'Programs',
        data: [],
        field: 'programId',
        valueField: 'value',
        textField: 'text',
    }
];
